import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useAuth } from '@/common/appContext';

import {
  postSelfOnboardingBranding,
  postSelfOnboardingRegister,
  postSelfOnboardingResend,
  postSelfOnboardingVerify,
  type SelfOnboardingBrandingPayload,
  type SelfOnboardingRegisterPayload,
} from './apiCalls';

function useCorrelationId(): string | null {
  const { correlationId } = useAuth();
  return correlationId ?? null;
}

export function useSelfOnboardingRegister() {
  const correlationId = useCorrelationId();
  const mutation = useMutation({
    mutationFn: (payload: SelfOnboardingRegisterPayload) =>
      postSelfOnboardingRegister(payload, correlationId),
  });

  const registerCompany = useCallback(
    (payload: SelfOnboardingRegisterPayload) => mutation.mutateAsync(payload),
    [mutation.mutateAsync]
  );

  return useMemo(
    () => ({
      loading: mutation.isPending,
      error: mutation.error,
      registerCompany,
    }),
    [mutation.error, mutation.isPending, registerCompany]
  );
}

export function useSelfOnboardingResend() {
  const correlationId = useCorrelationId();
  const mutation = useMutation({
    mutationFn: (payload: { email: string; language?: 'EN' | 'ES' }) =>
      postSelfOnboardingResend(payload, correlationId),
  });

  const resendVerification = useCallback(
    (payload: { email: string; language?: 'EN' | 'ES' }) => mutation.mutateAsync(payload),
    [mutation.mutateAsync]
  );

  return useMemo(
    () => ({
      loading: mutation.isPending,
      error: mutation.error,
      resendVerification,
    }),
    [mutation.error, mutation.isPending, resendVerification]
  );
}

export function useSelfOnboardingVerify() {
  const correlationId = useCorrelationId();
  const mutation = useMutation({
    mutationFn: (token: string) => postSelfOnboardingVerify(token, correlationId),
  });

  const verifyToken = useCallback(
    (token: string) => mutation.mutateAsync(token),
    [mutation.mutateAsync]
  );

  return useMemo(
    () => ({
      loading: mutation.isPending,
      error: mutation.error,
      verifyToken,
    }),
    [mutation.error, mutation.isPending, verifyToken]
  );
}

export function useSelfOnboardingBranding() {
  const correlationId = useCorrelationId();
  const mutation = useMutation({
    mutationFn: (payload: SelfOnboardingBrandingPayload) =>
      postSelfOnboardingBranding(payload, correlationId),
  });

  const completeBranding = useCallback(
    (payload: SelfOnboardingBrandingPayload) => mutation.mutateAsync(payload),
    [mutation.mutateAsync]
  );

  return useMemo(
    () => ({
      loading: mutation.isPending,
      error: mutation.error,
      completeBranding,
    }),
    [completeBranding, mutation.error, mutation.isPending]
  );
}
